import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Paper, Checkbox, Radio, RadioGroup,
    FormControlLabel, FormControl, TextField, Divider
} from '@mui/material';
import { courseService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const InstanceFeedbackSettings = ({ isLearningPath = false }) => {
    const { id, instanceId } = useParams();
    const { showToast } = useToast();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(false);
    const [itemData, setItemData] = useState(null);
    const [instanceData, setInstanceData] = useState(null);
    const [feedbackSettings, setFeedbackSettings] = useState({
        enabled: false,
        questionRequirement: 'first',
        question1: '',
        question2: '',
        q1Mandatory: false,
        q2Mandatory: false,
        showImmediate: false
    });

    useEffect(() => {
        loadData();
    }, [id, instanceId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load item data (course or learning path)
            let response;
            if (isLearningPath) {
                // Implement learning path data loading
            } else {
                response = await courseService.getCourseById(id);
                setItemData(response.data.data);
            }
            
            // Load feedback settings
            const settingsResponse = await courseService.getFeedbackSettings(id, instanceId, isLearningPath);
            
            if (settingsResponse.data && settingsResponse.data.settings) {
                const settings = settingsResponse.data.settings;
                setFeedbackSettings({
                    enabled: settings.enabled === 1,
                    questionRequirement: settings.question_requirement || 'first',
                    question1: settings.question1 || '',
                    question2: settings.question2 || '',
                    q1Mandatory: settings.question1_mandatory === 1,
                    q2Mandatory: settings.question2_mandatory === 1,
                    showImmediate: settings.show_immediate === 1
                });
            }
            
            // Load instance data
            if (isLearningPath) {
                // Implement learning path instance data loading
            } else {
                const instancesResponse = await courseService.getInstance(id);
                const instance = instancesResponse.data.data.instances.find(
                    inst => inst.id === parseInt(instanceId)
                );
                setInstanceData(instance);
            }
        } catch (error) {
            console.error("Error loading data:", error);
            showToast("Failed to load data", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFeedbackSettingsChange = (field, value) => {
        setFeedbackSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const saveFeedbackSettings = async () => {
        setLoading(true);
        try {
            await courseService.saveFeedbackSettings(id, instanceId, feedbackSettings, isLearningPath);
            showToast('Feedback settings saved successfully!', 'success');
            
            // Navigate back to the instances page
            const path = isLearningPath ? 
                `/learning_path/instances/${id}` : 
                `/course/instances/${id}`;
            navigate(path);
        } catch (error) {
            console.error("Error saving feedback settings:", error);
            showToast('Failed to save feedback settings', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        const path = isLearningPath ? 
            `/learning_path/instances/${id}` : 
            `/course/instances/${id}`;
        navigate(path);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Feedback Settings
            </Typography>
            
            {instanceData && (
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    Instance: {instanceData.title}
                </Typography>
            )}
            
            <Paper sx={{ p: 3, mt: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={feedbackSettings.enabled}
                            onChange={(e) => handleFeedbackSettingsChange('enabled', e.target.checked)}
                            disabled={loading}
                        />
                    }
                    label="Enable Reaction Feedback"
                />
                
                <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
                    A set of predefined questions for the Learner on completing the {isLearningPath ? 'Learning Path' : 'Course'}
                </Typography>
                
                {feedbackSettings.enabled && (
                    <Box sx={{ mt: 2, ml: 4 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Feedback Questions
                        </Typography>
                        
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={feedbackSettings.questionRequirement}
                                onChange={(e) => handleFeedbackSettingsChange('questionRequirement', e.target.value)}
                            >
                                <FormControlLabel 
                                    value="first" 
                                    control={<Radio />} 
                                    label="Make only First Question Required" 
                                    disabled={loading}
                                />
                                <FormControlLabel 
                                    value="all" 
                                    control={<Radio />} 
                                    label="Make All Questions Required" 
                                    disabled={loading}
                                />
                            </RadioGroup>
                        </FormControl>
                        
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Question 1
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter feedback question"
                                value={feedbackSettings.question1}
                                onChange={(e) => handleFeedbackSettingsChange('question1', e.target.value)}
                                inputProps={{ maxLength: 200 }}
                                helperText={`${feedbackSettings.question1.length}/200 characters`}
                                disabled={loading}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={feedbackSettings.q1Mandatory}
                                        onChange={(e) => handleFeedbackSettingsChange('q1Mandatory', e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Make mandatory"
                            />
                            <Typography variant="caption" color="textSecondary" display="block">
                                Answer Format: Long Answer (500 characters)
                            </Typography>
                        </Box>
                        
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                Question 2
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Enter feedback question"
                                value={feedbackSettings.question2}
                                onChange={(e) => handleFeedbackSettingsChange('question2', e.target.value)}
                                inputProps={{ maxLength: 200 }}
                                helperText={`${feedbackSettings.question2.length}/200 characters`}
                                disabled={loading}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={feedbackSettings.q2Mandatory}
                                        onChange={(e) => handleFeedbackSettingsChange('q2Mandatory', e.target.checked)}
                                        disabled={loading}
                                    />
                                }
                                label="Make mandatory"
                            />
                            <Typography variant="caption" color="textSecondary" display="block">
                                Answer Format: Long Answer (500 characters)
                            </Typography>
                        </Box>
                        
                        <Divider sx={{ my: 3 }} />
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={feedbackSettings.showImmediate}
                                    onChange={(e) => handleFeedbackSettingsChange('showImmediate', e.target.checked)}
                                    disabled={loading}
                                />
                            }
                            label={`Show questionnaire immediately after ${isLearningPath ? 'Learning Path' : 'Course'} completion`}
                        />
                    </Box>
                )}
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button 
                        variant="outlined" 
                        sx={{ mr: 1 }}
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained"
                        onClick={saveFeedbackSettings}
                        disabled={loading}
                    >
                        Save Changes
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default InstanceFeedbackSettings;
